import React, { useState, useContext, useEffect } from 'react';
import Icon from '../../common/Icon';
import FormMetaEntry from './FormMetaEntry';
import { createFormMetadata } from './handleFormMetaEntries';
import { FormContext } from '../../../hooks/Contexts';
import { notify } from '../../../helpers/notifications';

const FormMetaEntries = ({ show, toggle, editor }) => {
  const { form, formMetaEntries, setFormMetaEntries } = useContext(FormContext);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState(null);
  const toggleCreating = (e) => {
    e.stopPropagation();
    setCreating(!creating);
  };

  const handleCreateFormMetadata = async (e) => {
    e.preventDefault();
    let titleInput = document.getElementById('new-meta-title');
    let valueInput = document.getElementById('new-meta-value');
    let formMetadata = {
      title: titleInput.value,
      value: valueInput.value,
      form_id: form.id,
    };
    let response = await createFormMetadata(formMetadata);
    if (response.ok) {
      setFormMetaEntries((entries) => [...entries, response.data]);
      titleInput.value = '';
      valueInput.value = '';
      titleInput.focus();
    } else if (response.errors) {
      setErrors(response.errors);
      console.error(response);
    }
  };

  const handleQuickSave = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      handleCreateFormMetadata(e);
    }
  };

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, message]) => {
        notify.failure(
          `${key.charAt(0).toUpperCase() + key.slice(1)} ${message}`,
          { showOnlyTheLastOne: false }
        );
      });
      setErrors(null);
    }
  }, [errors]);

  useEffect(() => {
    if (!show) {
      setCreating(false);
    }
    determineHeight();
  }, [show, creating, formMetaEntries.length]);

  const determineHeight = () => {
    let details = document.getElementById('form-meta-details');
    if (details) {
      let height = details.scrollHeight;
      if (editor) {
        // Account for New Metadata input heights if creating
        height = creating ? height + 138 : height;
        // Account for error messages popping up for each entry
        height = height + formMetaEntries.length * 10;
      }
      details.style.maxHeight = show ? `${height}px` : '0px';
    }
  };

  const transitionStyle = {
    transition: 'max-height 0.3s ease-in-out',
    overflow: 'hidden',
  };

  return (
    <>
      <hr />
      <div className='row'>
        <div className='name'>
          <h3>Form Metadata</h3>
        </div>
        {(formMetaEntries.length > 0 || editor) && (
          <div className='value form-meta-icons'>
            {show && editor && !creating && (
              <Icon icon='plus' onClick={(e) => toggleCreating(e)} />
            )}
            <Icon
              title={show ? 'Hide' : 'Show'}
              className={'right'}
              icon={`chevron-${show ? 'up' : 'down'}`}
              onClick={toggle}
            />
          </div>
        )}
      </div>
      <div style={transitionStyle} id='form-meta-details'>
        {editor && (
          <div
            className='row'
            style={{
              maxHeight: creating ? '138px' : '0px',
              ...transitionStyle,
            }}
          >
            <div className='name'>New:</div>
            <div className='value'>
              <i>Quick Save: Ctrl + Enter</i>
              <div className='right form-meta-icons'>
                <Icon
                  icon='save'
                  title='Save'
                  onClick={(e) => handleCreateFormMetadata(e)}
                />
                <Icon icon='xmark' title='Cancel' onClick={toggleCreating} />
              </div>
              <input
                type='text'
                placeholder='Form Metadata Title (Required)'
                id='new-meta-title'
                style={{ marginBottom: '4px' }}
                onKeyDown={(e) => handleQuickSave(e)}
                autoFocus
              />
              <textarea
                rows={3}
                maxLength={500}
                placeholder='Value (500 char max, Required)'
                onKeyDown={(e) => handleQuickSave(e)}
                id='new-meta-value'
              />
            </div>
          </div>
        )}
        {formMetaEntries.length
          ? formMetaEntries
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((fme) => (
                <FormMetaEntry
                  key={`fme-${fme.id}`}
                  formMetaEntry={fme}
                  editor={editor}
                  show={show}
                  error={
                    editor?.metaErrors?.find((metaErr) => metaErr.id == fme.id)
                      ?.error
                  }
                />
              ))
          : !creating && <div className='name'>None</div>}
      </div>
    </>
  );
};

export default FormMetaEntries;
